/**
 * Contact Service Layer
 * Handles all Firestore operations for customer contacts subcollection
 * Subcollection path: customers/{customerId}/contacts/{contactId}
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  writeBatch,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Contact } from '../types/schema';
import { getTenantIdSync } from '../hooks/useTenantId';

// Strip undefined values from a contact payload — Firestore rejects undefined
// (e.g. addressIds left blank, optional phone/email/role/notes). Without this,
// adding a contact with any empty optional field throws and the save silently
// fails for the user.
function stripUndefined<T extends Record<string, unknown>>(data: T): T {
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined)
  ) as T;
}

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new contact for a customer
 */
export async function createContact(
  customerId: string,
  data: Omit<Contact, 'id' | 'customerId' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString();

  const contactData = stripUndefined({
    ...data,
    customerId,
    tenantId: (data as { tenantId?: string }).tenantId ?? getTenantIdSync(),
    createdAt: now,
    updatedAt: now,
  }) as Omit<Contact, 'id'>;

  const contactsRef = collection(db, 'customers', customerId, 'contacts');
  const docRef = await addDoc(contactsRef, contactData);
  return docRef.id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single contact by ID
 */
export async function getContact(
  customerId: string,
  contactId: string
): Promise<Contact | null> {
  const docRef = doc(db, 'customers', customerId, 'contacts', contactId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Contact;
}

/**
 * Get all contacts for a customer
 */
export async function getCustomerContacts(customerId: string): Promise<Contact[]> {
  const contactsRef = collection(db, 'customers', customerId, 'contacts');
  const q = query(contactsRef, orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Contact
  );
}

/**
 * Get primary contact for a customer
 */
export async function getPrimaryContact(customerId: string): Promise<Contact | null> {
  const contactsRef = collection(db, 'customers', customerId, 'contacts');
  const q = query(contactsRef, where('isPrimary', '==', true));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  const doc = querySnapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as Contact;
}

/**
 * Subscribe to real-time updates for customer contacts
 */
export function subscribeToCustomerContacts(
  customerId: string,
  callback: (contacts: Contact[]) => void
): Unsubscribe {
  const contactsRef = collection(db, 'customers', customerId, 'contacts');
  const q = query(contactsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const contacts = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Contact
    );
    callback(contacts);
  });
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a contact
 */
export async function updateContact(
  customerId: string,
  contactId: string,
  data: Partial<Omit<Contact, 'id' | 'customerId' | 'createdAt'>>
): Promise<void> {
  const docRef = doc(db, 'customers', customerId, 'contacts', contactId);
  const now = new Date().toISOString();

  await updateDoc(docRef, stripUndefined({
    ...data,
    updatedAt: now,
  }));
}

/**
 * Set a contact as primary (and unset other primary contacts)
 */
export async function setPrimaryContact(
  customerId: string,
  contactId: string
): Promise<void> {
  // First, get all contacts and unset primary flag
  const contacts = await getCustomerContacts(customerId);

  const updatePromises = contacts.map(async (contact) => {
    if (contact.id === contactId) {
      // Set this one as primary
      await updateContact(customerId, contactId, { isPrimary: true });
    } else if (contact.isPrimary) {
      // Unset other primary contacts
      await updateContact(customerId, contact.id!, { isPrimary: false });
    }
  });

  await Promise.all(updatePromises);
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a contact
 */
export async function deleteContact(
  customerId: string,
  contactId: string
): Promise<void> {
  const docRef = doc(db, 'customers', customerId, 'contacts', contactId);
  await deleteDoc(docRef);
}

// ============================================================================
// ROLE-BASED HELPERS (V107.52.32)
//
// A Contact may carry two independent role flags: `account` (billing/decisions)
// and `site` (day-to-day, on-site access). One person can serve as both. Legacy
// contacts without explicit flags are treated as both for backward compatibility.
// ============================================================================

/**
 * Determine whether a contact fills the given role.
 * Legacy contacts (no contactType / no explicit primary flags) fall back to both roles.
 */
export function contactHasRole(
  contact: Contact,
  role: 'account' | 'site',
): boolean {
  const hasExplicit =
    (contact.contactType && contact.contactType.length > 0) ||
    contact.isPrimaryAccount !== undefined ||
    contact.isPrimarySite !== undefined;

  if (!hasExplicit) return true;

  if (contact.contactType?.includes(role)) return true;
  if (role === 'account' && contact.isPrimaryAccount) return true;
  if (role === 'site' && contact.isPrimarySite) return true;

  return false;
}

/**
 * Get the primary account (billing) contact for a customer.
 * Resolution order:
 *   1. Contact with isPrimaryAccount: true
 *   2. Legacy isPrimary flag with account role (or no role set)
 *   3. Any contact with account role
 */
export async function getPrimaryAccountContact(
  customerId: string,
): Promise<Contact | null> {
  const contacts = await getCustomerContacts(customerId);
  if (contacts.length === 0) return null;

  const byFlag = contacts.find((c) => c.isPrimaryAccount);
  if (byFlag) return byFlag;

  const byLegacy = contacts.find(
    (c) => c.isPrimary && contactHasRole(c, 'account'),
  );
  if (byLegacy) return byLegacy;

  const firstAccount = contacts.find((c) => contactHasRole(c, 'account'));
  return firstAccount || null;
}

/**
 * Get the primary site contact for a customer, optionally scoped to a specific address.
 * Resolution order:
 *   1. Contact explicitly linked to this address via addressIds (first match)
 *   2. Contact with isPrimarySite: true (applies to all sites when addressIds is empty/unset)
 *   3. Legacy isPrimary flag with site role
 *   4. Any contact with site role
 *   5. Falls back to primary account contact as a last resort (caller's choice to use)
 */
export async function getPrimarySiteContact(
  customerId: string,
  addressId?: string,
): Promise<Contact | null> {
  const contacts = await getCustomerContacts(customerId);
  if (contacts.length === 0) return null;

  if (addressId) {
    const addressSpecific = contacts.find(
      (c) => c.addressIds?.includes(addressId) && contactHasRole(c, 'site'),
    );
    if (addressSpecific) return addressSpecific;
  }

  const generalSite = contacts.find(
    (c) =>
      c.isPrimarySite &&
      (!c.addressIds || c.addressIds.length === 0),
  );
  if (generalSite) return generalSite;

  const byLegacy = contacts.find(
    (c) => c.isPrimary && contactHasRole(c, 'site'),
  );
  if (byLegacy) return byLegacy;

  const firstSite = contacts.find((c) => contactHasRole(c, 'site'));
  return firstSite || null;
}

/**
 * Set a contact as the primary account (billing) contact — clears the flag on
 * all other contacts for this customer so there's only one primary at a time.
 *
 * Uses a Firestore batch so every flag flip commits atomically — a network
 * failure mid-update can't leave us with two primaries or zero.
 */
export async function setPrimaryAccountContact(
  customerId: string,
  contactId: string,
): Promise<void> {
  const contacts = await getCustomerContacts(customerId);
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  for (const c of contacts) {
    if (!c.id) continue;
    const ref = doc(db, 'customers', customerId, 'contacts', c.id);
    if (c.id === contactId) {
      batch.update(ref, { isPrimaryAccount: true, updatedAt: now });
    } else if (c.isPrimaryAccount) {
      batch.update(ref, { isPrimaryAccount: false, updatedAt: now });
    }
  }

  await batch.commit();
}

/**
 * Set a contact as the primary site contact.
 * If addressId is provided, it's set as primary for that specific address only
 * (doesn't clear other general site primaries — a per-address override is not
 * mutually exclusive with a general site primary for other addresses).
 */
export async function setPrimarySiteContact(
  customerId: string,
  contactId: string,
  addressId?: string,
): Promise<void> {
  if (addressId) {
    const target = await getContact(customerId, contactId);
    if (!target) return;
    const nextAddresses = Array.from(
      new Set([...(target.addressIds || []), addressId]),
    );
    const types = Array.from(new Set([...(target.contactType || []), 'site'])) as (
      | 'account'
      | 'site'
    )[];
    await updateContact(customerId, contactId, {
      addressIds: nextAddresses,
      contactType: types,
    });
    return;
  }

  const contacts = await getCustomerContacts(customerId);
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  for (const c of contacts) {
    if (!c.id) continue;
    const ref = doc(db, 'customers', customerId, 'contacts', c.id);
    if (c.id === contactId) {
      batch.update(ref, { isPrimarySite: true, updatedAt: now });
    } else if (c.isPrimarySite) {
      batch.update(ref, { isPrimarySite: false, updatedAt: now });
    }
  }

  await batch.commit();
}
